import req from '@sportninja/common/api/request'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

const useScheduleList = (
  slug,
  {
    initialSort = 'ends_at',
    initialDirection = 'desc',
    alwaysListArchived = false,
    initialMembershipType = 'following',
    queryRecursive = false,
    externalMembershipType = null,
    onMembershipTypeChangeExternal = null,
  } = {}
) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(initialSort)
  const [direction, setDirection] = useState(initialDirection)
  const [totalCount, setTotalCount] = useState(0)
  const [permissions, setPermissions] = useState({})
  const [sportFilter, setSportFilter] = useState('')
  const [showArchived, setShowArchived] = useState(alwaysListArchived || false)
  const [internalMembershipType, setInternalMembershipType] =
    useState(initialMembershipType)
  const [organizationId, setOrganizationId] = useState('')
  const [error, setError] = useState(null)

  // Use external value if provided (controlled mode), otherwise internal state
  const membershipType = externalMembershipType ?? internalMembershipType

  const debouncedSearch = useDebounce(search, 500)

  // Store sportFilterList in a ref so it can be accessed in callbacks
  const sportFilterListRef = useRef([])

  const fetchItems = useCallback(
    async (
      currentPage,
      currentSort,
      currentDirection,
      currentSearch,
      currentSportFilterValue,
      currentShowArchived,
      currentOrganizationId,
      isAppend = false
    ) => {
      if (!slug) {
        return
      }

      setLoading(true)
      setError(null)
      try {
        const query = {
          page: currentPage,
          sort: currentSort,
          direction: currentDirection,
          is_archive: currentShowArchived ? 1 : 0,
          membership_type: membershipType,
          ...(currentSearch && { query: currentSearch }),
          ...(currentOrganizationId && {
            organization_id: currentOrganizationId,
          }),
        }

        if (queryRecursive) {
          query.recursive = 1
        }

        // Handle sport filter with generic sports support
        if (currentSportFilterValue && sportFilterListRef.current.length > 0) {
          const sport = sportFilterListRef.current.find(
            (s) => s.value === currentSportFilterValue
          )
          if (sport) {
            if (sport.is_generic && sport.generic_sport_id) {
              query.sport_id = sport.generic_sport_id
              query.generic_sport_id = currentSportFilterValue
            } else {
              query.sport_id = currentSportFilterValue
            }
          }
        }

        // /v1/organizations/{orgId}/schedules/V2?page=1&sort=ends_at&direction=desc&is_archive=0&recursive=1

        let url = slug
        if (currentOrganizationId) {
          url = `/organizations/${currentOrganizationId}/schedules/V2`
        }

        const response = await req(url, { query })

        const newItems = response.data || []
        const meta = response.meta || {}
        const pagination = meta.pagination || {}
        const newPermissions = meta.permissions || {}

        setPermissions((prev) =>
          isAppend ? { ...prev, ...newPermissions } : newPermissions
        )
        setItems((prev) => (isAppend ? [...prev, ...newItems] : newItems))
        setTotalCount(pagination.total || 0)

        const totalPages = pagination.total_pages || 1
        setHasMore(currentPage < totalPages)
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    [slug, membershipType, queryRecursive]
  )

  // Initial load and search/sort/filter changes
  useEffect(() => {
    if (!slug) {
      return
    }
    setPage(1)
    fetchItems(
      1,
      sort,
      direction,
      debouncedSearch,
      sportFilter,
      showArchived,
      organizationId,
      false
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sort,
    direction,
    debouncedSearch,
    sportFilter,
    showArchived,
    membershipType,
    organizationId,
    slug,
  ])

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchItems(
        nextPage,
        sort,
        direction,
        debouncedSearch,
        sportFilter,
        showArchived,
        organizationId,
        true
      )
    }
  }, [
    loading,
    hasMore,
    page,
    sort,
    direction,
    debouncedSearch,
    sportFilter,
    showArchived,
    organizationId,
    fetchItems,
  ])

  const handleSearch = useCallback((value) => {
    setSearch(value)
  }, [])

  const handleSort = useCallback((newSort, newDirection) => {
    setSort(newSort)
    setDirection(newDirection)
  }, [])

  const handleSportFilter = useCallback((value, sportFilterList) => {
    if (sportFilterList) {
      sportFilterListRef.current = sportFilterList
    }
    setSportFilter(value)
  }, [])

  const handleShowArchived = useCallback((value) => {
    setShowArchived(value)
  }, [])

  const handleMembershipType = useCallback(
    (value) => {
      setInternalMembershipType(value)
      onMembershipTypeChangeExternal?.(value)
    },
    [onMembershipTypeChangeExternal]
  )

  const handleOrganizationIdChange = useCallback((value) => {
    setOrganizationId(value)
  }, [])

  const refresh = useCallback(() => {
    setPage(1)
    fetchItems(
      1,
      sort,
      direction,
      debouncedSearch,
      sportFilter,
      showArchived,
      organizationId,
      false // isAppend = false to replace items
    )
  }, [
    fetchItems,
    sort,
    direction,
    debouncedSearch,
    sportFilter,
    showArchived,
    organizationId,
  ])

  // Method to update sportFilterList reference
  const updateSportFilterList = useCallback((list) => {
    sportFilterListRef.current = list
  }, [])

  return {
    items,
    loading,
    hasMore,
    totalCount,
    permissions,
    search,
    sportFilter,
    showArchived,
    membershipType,
    organizationId,
    handleSearch,
    handleSort,
    handleSportFilter,
    handleShowArchived,
    handleMembershipType,
    handleOrganizationIdChange,
    loadMore,
    refresh,
    sort,
    direction,
    updateSportFilterList,
    error,
  }
}

export default useScheduleList
